import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Checkbox } from "primereact/checkbox";
import { useToast } from "../../context/ToastContext";
import GroupApi from "../../services/GroupApi";
import UserApi, { UpdateUserPayload } from "../../services/UserApi";
import { formatPhone, isValidPhone, sanitizePhone } from "../../utilities/phone";

// Design System
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import Input from "../../components/Input/Input";
import FormCard from "../../components/FormCard/FormCard";

type GroupOption = {
  id: string;
  name: string;
  color?: string;
};

type DropdownTemplateProps = {
  placeholder?: string;
};

const EditUserForm: React.FC = () => {
  const params = useParams<{ id?: string; userId?: string }>();
  const userId = params.userId ?? params.id ?? "";

  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");

  const [name, setName] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [initialGroupId, setInitialGroupId] = useState<string>("");
  const [userGroupSnapshot, setUserGroupSnapshot] = useState<GroupOption | null>(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const groupIdValue = selectedGroup?.id ?? initialGroupId ?? "";

  const nameRef = useRef<any>(null);
  const userNameRef = useRef<any>(null);
  const emailRef = useRef<any>(null);
  const phoneRef = useRef<any>(null);
  const departamentRef = useRef<any>(null);
  const positionRef = useRef<any>(null);
  const groupIdRef = useRef<any>(null);

  const extractErrorMessage = useCallback((error: any): string => {
    if (!error) {
      return "";
    }

    const candidates: any[] = [
      error?.data?.error?.message,
      error?.data?.message,
      Array.isArray(error?.errors)
        ? error.errors.find((msg: any) => typeof msg === "string" && msg.trim().length > 0)
        : null,
      error?.response?.data?.error?.message,
      error?.response?.data?.message,
      error?.message,
    ];

    const match = candidates.find((entry) => typeof entry === "string" && entry.trim().length > 0);
    return match ? match.trim() : "";
  }, []);

  const showErrorToast = useCallback(
    (error: any, duration = 3000, fallbackDetail?: string): void => {
      const summary = translateMessages("error") || "Erro";
      const detail = extractErrorMessage(error) || fallbackDetail || summary;
      displayToast("error", summary, detail, false, duration);
    },
    [displayToast, extractErrorMessage, translateMessages],
  );

  const isValidEmail = (val: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const isValidGuid = (val: string): boolean =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(val.trim());

  useEffect(() => {
    if (!userId) {
      displayToast(
        "error",
        translateMessages("error"),
        translateForm("userNotFound") || "Usuário não encontrado",
        false,
        3000,
      );
      navigate("/users");
    }
  }, [displayToast, navigate, translateForm, translateMessages, userId]);

  useEffect(() => {
    const loadGroups = async (): Promise<void> => {
      try {
        setGroupsLoading(true);
        const response = await GroupApi.ListGroups();
        const rawList: any[] = Array.isArray(response?.data)
          ? (response.data as any[])
          : Array.isArray(response)
            ? (response as any[])
            : [];
        const normalized = rawList
          .map((item) => {
            const id = item?.id ?? item?.groupId ?? item?.groupID ?? item?.Id ?? "";
            const name = item?.name ?? item?.groupName ?? item?.description ?? item?.GroupName ?? "";
            if (!id) {
              return null;
            }
            return {
              id,
              name: name || id,
              color: item?.color ?? item?.groupColor ?? undefined,
            } as GroupOption;
          })
          .filter((entry): entry is GroupOption => Boolean(entry));
        setGroups(normalized);
      } catch (err) {
        showErrorToast(err, 3000, translateMessages("groupsLoadFailed") || "Falha ao carregar grupos");
      } finally {
        setGroupsLoading(false);
      }
    };

    loadGroups();
  }, [showErrorToast, translateMessages]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const loadUser = async (): Promise<void> => {
      try {
        setLoadingUser(true);
        const response = await UserApi.GetById(userId);
        const data: any = response?.data ?? response;

        setName(data?.name ?? "");
        setUserName(data?.username ?? data?.userName ?? "");
        setEmail(data?.email ?? "");
        setPhone(formatPhone(data?.phone ?? data?.phoneNumber ?? ""));
        setDepartment(data?.departament ?? data?.department ?? "");
        setPosition(data?.position ?? "");
        setIsActive(
          typeof data?.active === "boolean"
            ? data.active
            : typeof data?.isActive === "boolean"
              ? data.isActive
              : true,
        );
        setError(false);

        const groupIdFromApi: string =
          data?.groupId ??
          data?.groupID ??
          data?.group?.id ??
          data?.group?.groupId ??
          "";
        const groupOptionFromApi: GroupOption | null = groupIdFromApi
          ? {
              id: groupIdFromApi,
              name:
                data?.group?.name ??
                data?.groupName ??
                data?.group?.description ??
                groupIdFromApi,
              color: data?.group?.color ?? data?.groupColor ?? undefined,
            }
          : null;

        setInitialGroupId(groupIdFromApi);
        setUserGroupSnapshot(groupOptionFromApi);
        setSelectedGroup(groupOptionFromApi);
      } catch (err) {
        showErrorToast(err, 4000, translateForm("userLoadFailed") || "Não foi possível carregar o usuário");
        navigate("/users");
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [navigate, showErrorToast, translateForm, userId]);

  useEffect(() => {
    if (!initialGroupId) {
      setSelectedGroup(null);
      return;
    }

    setSelectedGroup((prev) => {
      if (prev?.id === initialGroupId) {
        return prev;
      }

      const match = groups.find((item) => item.id === initialGroupId);
      if (match) {
        return match;
      }

      return userGroupSnapshot ?? prev;
    });
  }, [groups, initialGroupId, userGroupSnapshot]);

  const renderDropdownValueTemplate = (
    option: GroupOption | null,
    props: DropdownTemplateProps,
  ): React.ReactNode => {
    const resolvedOption = option ?? (groupIdValue ? groups.find((item) => item.id === groupIdValue) ?? null : null);

    if (!resolvedOption) {
      return <span>{props.placeholder}</span>;
    }

    return (
      <div className="flex align-items-center gap-2">
        {resolvedOption.color && (
          <span
            style={{
              width: 12,
              height: 12,
              backgroundColor: resolvedOption.color,
              display: "inline-block",
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,0.2)",
            }}
          />
        )}
        <span>{resolvedOption.name}</span>
      </div>
    );
  };

  const renderDropdownItemTemplate = (option: GroupOption): React.ReactNode => (
    <div className="flex align-items-center gap-2">
      {option.color && (
        <span
          style={{
            width: 12,
            height: 12,
            backgroundColor: option.color,
            display: "inline-block",
            borderRadius: "50%",
            border: "1px solid rgba(0,0,0,0.2)",
          }}
        />
      )}
      <span>{option.name}</span>
    </div>
  );



  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!userId) {
      displayToast(
        "error",
        translateMessages("error"),
        translateForm("userNotFound") || "Usuário não encontrado",
        false,
        3000,
      );
      return;
    }

    const trimmedName = name.trim();
    const trimmedUserName = userName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const sanitizedPhone = sanitizePhone(trimmedPhone);
    const trimmedDepartament = department.trim();
    const trimmedPosition = position.trim();

    const hasEmpty =
      !trimmedName ||
      !trimmedUserName ||
      !trimmedEmail ||
      !sanitizedPhone ||
      !groupIdValue;

    if (hasEmpty) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("requiredFields"),
        false,
        3000,
      );
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("invalidEmail") || "E-mail inválido",
        false,
        3000,
      );
      emailRef.current?.focus();
      return;
    }

    if (!isValidPhone(trimmedPhone)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("invalidPhone") || translateForm("invalidPhone") || "Telefone inválido",
        false,
        3000,
      );
      phoneRef.current?.focus();
      return;
    }

    if (!groupIdValue || !isValidGuid(groupIdValue)) {
      setError(true);
      displayToast(
        "error",
        translateMessages("error"),
        translateMessages("invalidGroup") || "Grupo inválido",
        false,
        3000,
      );
      groupIdRef.current?.focus?.();
      return;
    }

    const payload: UpdateUserPayload = {
      Id: userId, 
      Name: trimmedName,
      Username: trimmedUserName,
      Email: trimmedEmail,
      Phone: sanitizedPhone,
      Department: trimmedDepartament ? trimmedDepartament : null,
      Position: trimmedPosition ? trimmedPosition : null,
      GroupId: groupIdValue,
      Active: isActive,
    };

    try {
      setSaving(true);
      await UserApi.Update(userId, payload);
      displayToast(
        "success",
        translateForm("Success"),
        translateForm("userUpdated") || "Usuário atualizado com sucesso",
        false,
        3000,
      );
      navigate("/users");
    } catch (err) {
      showErrorToast(err, 4000, translateForm("userNotUpdated") || "Não foi possível atualizar o usuário");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <FormCard
        title={translateForm("editUser") || "Editar Usuário"}
        icon="edit"
        onSubmit={handleSubmit}
        onCancel={() => setShowConfirmCancel(true)}
        cancelButtonLabel={translateForm("back") || "Voltar"}
        submitButtonLabel={saving ? translateForm("saving") || "Salvando..." : translateForm("save") || "Salvar"}
        numColumns={12}
      >
        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="name"
            ref={nameRef}
            type="text"
            label={translateForm("name") || "Nome"}
            value={name}
            disabled={loadingUser || saving}
            onChange={(val) => { setName(val); setError(false); }}
            required
            className={error && !name.trim() ? "p-invalid border-red" : ""}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="userName"
            ref={userNameRef}
            type="text"
            label={translateForm("userName") || "Usuário"}
            value={userName}
            disabled={loadingUser || saving}
            onChange={(val) => { setUserName(val); setError(false); }}
            required
            className={error && !userName.trim() ? "p-invalid border-red" : ""}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="email"
            ref={emailRef}
            type="text"
            label={translateForm("email") || "E-mail"}
            value={email}
            disabled={loadingUser || saving}
            onChange={(val) => { setEmail(val); setError(false); }}
            required
            className={error && (!email.trim() || !isValidEmail(email)) ? "p-invalid border-red" : ""}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="phone"
            ref={phoneRef}
            type="text"
            label={translateForm("phone") || "Telefone"}
            value={phone}
            disabled={loadingUser || saving}
            onChange={(val) => { setPhone(formatPhone(val)); setError(false); }}
            required
            className={error && (!phone.trim() || !isValidPhone(phone.trim())) ? "p-invalid border-red" : ""}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="department"
            ref={departamentRef}
            type="text"
            label={translateForm("departament") || "Departamento"}
            value={department}
            disabled={loadingUser || saving}
            onChange={(val) => { setDepartment(val); setError(false); }}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="position"
            ref={positionRef}
            type="text"
            label={translateForm("position") || "Cargo"}
            value={position}
            disabled={loadingUser || saving}
            onChange={(val) => { setPosition(val); setError(false); }}
          />
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <Input
            id="groupId"
            ref={groupIdRef}
            type="select"
            label={translateForm("group") || "Grupo"}
            value={groupIdValue}
            options={groups}
            optionLabel="name"
            optionValue="id"
            disabled={groupsLoading || loadingUser || saving}
            onChange={(val) => { 
                const match = groups.find((item) => item.id === val) ?? null;
                setSelectedGroup(match);
                setInitialGroupId(val);
                setError(false);
             }}
            required
            valueTemplate={renderDropdownValueTemplate}
            itemTemplate={renderDropdownItemTemplate}
            loading={groupsLoading}
            className={error && (!groupIdValue || !isValidGuid(groupIdValue)) ? "p-invalid border-red" : ""}
            filter
            showClear
          />
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="isActive"
              checked={isActive}
              onChange={(e) => {
                setIsActive(Boolean(e.checked));
                setError(false);
              }}
              disabled={saving}
            />
            <label htmlFor="isActive" className="text-sm m-0">
              {translateForm("active") || translateMessages("active") || "Ativo"}
            </label>
          </div>
        </div>
      </FormCard>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/users")}
      />
    </>
  );
};

export default EditUserForm;
